import FinancialReviewQuery from '../../financial_review/queries/FinancialReviewQuery'
import LoanCashflowQuery from '../../financial_review/queries/LoanCashflowQuery'
import BusinessCashflowQuery from '../../financial_review/queries/BusinessCashflowQuery'
import GuarantorCashflowQuery from '../../financial_review/queries/GuarantorCashflowQuery'

// TODO: Make this a class that takes an ApolloClient instance and make it widely available using the context API
// This will allow the class to import any necessary queries for making updates and require fewer params
export default {
  /**
   * Find an entity within a loan (Business or Guarantor)
   * @param {object} entity The entity to find in the query
   * @param {string} entity.__typename The type of entity to find
   * @param {number} entity.rid The entity's rid
   * @param {object} entity.business The business that the entity belongs to (only necessary with Guarantors)
   */
  findEntityInQuery({ __typename, rid, business }, data) {
    if (__typename === 'Business') {
      return data.loan.businesses.find(b => b.rid === rid)
    } else if (__typename === 'Guarantor') {
      return data.loan.businesses.find(b => b.rid === business.rid).guarantors.find(g => g.rid === rid)
    }
  },
  /**
 * Find an entity by it's id within a loan
 * @param {object} loan The loan in which the entity will be searched for
 * @param {number} rid  The id of the entity being searched for
 * @param {string} resourceType The `__typename` property of the entity being searched for ('Business' or 'Guarantor')
 */
  findEntityById(loan, rid, resourceType) {
    const entities = this.getEntities(loan)
    return entities.find(e => e.rid === rid && e.__typename === resourceType)
  },
  /**
   * Get a flat array of all the entities in a loan
   * @param {object} loan The loan from which entities will be retrieved
   */
  getEntities(loan) {
    return loan.businesses.concat(loan.businesses.reduce((acc, b) => acc.concat(b.guarantors), []))
  },
  /**
   * Find an affilite entity by it's id within a loan
   * @param {object} loan The loan in which the entity will be searched for
   * @param {number} rid  The id of the entity being searched for
   */
  findAffiliateEntityById(loan, rid) {
    const entities = loan.affiliates
    return entities.find(e => e.rid === rid)
  },
  /**
   * Refetch an entity (or loan)'s cashflow data for the specified years and write it to the cache
   * @param {object}   client An instance of the Apollo client
   * @param {object}   queryVariables The entity and years to be refetched
   * @param {object}   queryVariables.entity The entity whose cashflow data is to be refetched (business, guarantor, or loan)
   * @param {number[]} years The years to be refetched
   * @param {object}   cashflowOptions Cashflow options to be passed with the cashflow query
   * @param {object}   cachedQueryVars Variables from the cached query that will be updated with new cashflow data
   * @param {boolean}  derivedOnly Whether or not only derived values should be refetched
   */
  async refetchEntityCashflowData(client, { entity, years, cashflowOptions }, cachedQueryVars, derivedOnly) {
    const { rid, __typename } = entity
    let query
    switch (__typename) {
      case 'Loan':
        query = LoanCashflowQuery
        break
      case 'Guarantor':
        query = GuarantorCashflowQuery
        break
      case 'Business':
        query = BusinessCashflowQuery
        break
      default:
        throw new Error('Invalid entity type.')
    }
    const queryVariables = {
      query,
      variables: {
        rid,
        cashflowOptions,
        years
      },
      fetchPolicy: 'no-cache'
    }
    const cachedQueryVariables = {
      query: FinancialReviewQuery,
      variables: cachedQueryVars
    }
    const data                = client.readQuery(cachedQueryVariables)
    const cachedEntity        = __typename === 'Loan' ? data.loan : this.findEntityInQuery(entity, data)
    const queryResponse       = await client.query(queryVariables)
    const queriedEntity       = __typename === 'Loan' ? queryResponse.data.loan : queryResponse.data[__typename.toLowerCase()]
    // If derivedOnly is true, we only filter out the entity's cashflow data that has a source of derived AND is
    // included in the years that are being refetched
    const oldCashflowData = cachedEntity.cashflowData.filter(d => {
      if (derivedOnly) {
        if (d.source === 'derived' && years.includes(d.year)) {
          return false
        }
      }
      return true
    })
    // If derivedOnly is true, we only want to concat the derived data fetched from the server
    const newCashflowData = queriedEntity.cashflowData.filter(d => {
      if (derivedOnly) {
        return d.source === 'derived'
      }
      return true
    })
    cachedEntity.cashflowData = oldCashflowData.concat(newCashflowData)
    client.writeQuery({ ...cachedQueryVariables, data })
  },
  /**
   * Replace a single datum on an entity read from the Apollo cache
   * If a matching datum is not found it will be added to the entity's cashflow data
   * @param {object} datum The cashflow datum to be used as a replacement
   * @param {object} entity The entity whose cashflow datum will be replaced
   */
  replaceCashflowDatum(datum, entity) {
    const { year, category } = datum
    const idx = entity.cashflowData.findIndex(d => d.year === year && d.category === category)
    if (idx > -1) {
      entity.cashflowData[idx] = datum
    } else {
      entity.cashflowData.push(datum)
    }
  },
  /**
   * Removes a single cashflow datum from an entity read from the Apollo cache
   * If the specified datum is not found, no action is taken
   * @param {object} dataKey Object containing information with which to locate the cashflow datum
   * @param {number} dataKey.year The year of the cashflow datum to be removed
   * @param {string} dataKey.category The category of the datum to be removed
   * @param {string} dataKey.source The source of the datum to be removed
   * @param {object} entity The entity whose cashflow datum will be removed
   */
  removeCashflowDatum({ year, category, source }, entity) {
    const idx = entity.cashflowData.findIndex(d => d.year === year && d.category === category && d.source === source)
    if (idx > -1) {
      entity.cashflowData.splice(idx, 1)
    }
  },
  /**
   * Removes all cashflow data from an entity read from the Apollo cache with matching year and category
   * @param {object} options Object containing the year and category to be removed
   * @param {number} options.year The year of the cashflow data to be removed
   * @param {string} category The category of the cashflow data to be removed
   * @param {object} entity An entity read from the Apollo Cache
   */
  removeCashflowDataByYearAndCategory({ year, category }, entity) {
    entity.cashflowData = entity.cashflowData.filter(d => d.year !== year || d.category !== category)
  },
  /**
   * Removes all cashflow data with the specified year from an entity read from the Apollo cache
   * @param {object}   entity An entity read from the Apollo Cache
   * @param {number}   year The year by which to remove cashflow data
   * @param {string[]} categoryExclusions categories to exclude from removal
   */
  removeCashflowDataByYear(entity, year, categoryExclusions = []) {
    entity.cashflowData = entity.cashflowData.filter(d => d.year !== year || categoryExclusions.includes(d.category))
  },
  /**
   * Gets the OC's applicable years on a given loan
   * @param {object} loan The loan from which applicableYears will be retrieved
   */
  getApplicableYears(loan) {
    return loan.businesses.find(b => b.borrowerType === 'oc').applicableYears
  },
  /**
   * Replaces an entity's fiscal year with the given fiscal year
   * @param {object} entity An entity read from the Apollo cache (Business or Guarantor)
   * @param {object} fiscalYear The fiscal year to be used as a replacement
   */
  replaceFiscalYear(entity, fiscalYear) {
    const idx = entity.fiscalYears.findIndex(fy => fy.year === fiscalYear.year)
    entity.fiscalYears[idx] = fiscalYear
  },
  /**
   *
   * @param {object} guarantor The guarantor whose spouse will be searched for
   * @param {object} loan The loan associated with the guarantor
   */
  findSpouse(guarantor, loan) {
    const { spouseId } = guarantor

    if (!spouseId) {
      return null
    }

    const entities = this.getEntities(loan)
    const spouse = entities.find(e => e.rid === spouseId && e.__typename === 'Guarantor')
    return spouse
  },
  /**
   *
   * @param {object} loan The loan to search for the OC in
   * @returns {object} The OC on the loan, or undefined if it is not found
   */
  findOC(loan) {
    return loan.businesses.find(b => b.borrowerType === 'oc')
  },
  updateDocument(entity, doc, loan) {
    const entities = this.getEntities(loan)
    const cachedEntity = entities.find(e => e.rid === entity.rid)
    const idx = cachedEntity.documents.findIndex(d => d.rid === doc.rid)
    if (idx > -1) {
      cachedEntity.documents[idx] = doc
    }
  }
}
